import * as z from 'zod';

const _currencies = ['AUD', 'EURO', 'USD', 'NZD', 'CAD'];
const zCurrency = z.preprocess(
  (v) => (v === 'EUR' ? 'EURO' : v),
  z
    .literal(_currencies)
    .nullish()
    .transform((v) => v || 'AUD'),
);

const zInvoiceStatus = z.literal([0, 10, 20]);
const zPurchaseOrder = z.coerce.number().catch(0);
const zHourlyRate = z
  .number()
  .nullish()
  .transform((v) => v ?? 175);
const zHoursValue = z
  .number()
  .nullish()
  .transform((v) => v || 0);

const zCommissionable = z.boolean().default(false);
const zProductStatus = z.literal(['Unknown', 'Prospective', 'Active', 'Inactive', 'On Hold']);
const zAgreementStatus = z.literal(['Active', 'Pending', 'Expired']);
const zBillingCycle = z.literal(['Monthly', 'Quarterly', 'Bi-annually', 'Yearly']);
const zServiceType = z.literal([
  'AWS Hosting',
  'AWS + S&M',
  'DSE Shared Hosting',
  'DSE + S&M',
  'Support & Maintenance',
  'GitHub Subscription',
  'SSL Certificate',
]);
const zProductOwner = z
  .string()
  .nullish()
  .transform((v) => v ?? '');

const zBudgetNote = z
  .string()
  .nullish()
  .transform((v) => v ?? '');
const zAddonName = z.literal([
  'SSL Certificate',
  'Development Hosting',
  'Reservation Costs',
  'Enterprise Account Management',
  'External Software',
  'Additional Storage',
]);

export const ProductStatuses = Array.from(zProductStatus.values).sort();
export const Currencies = _currencies.sort();
export const InvoiceStatuses = Array.from(zInvoiceStatus.values).sort();
export const AgreementStatuses = Array.from(zAgreementStatus.values);
export const BillingCycles = Array.from(zBillingCycle.values);
export const ServiceTypes = Array.from(zServiceType.values).sort();
export const AddonNames = Array.from(zAddonName.values).sort();

export const Roles = {
  ROLE_USER: 'User',
  ROLE_EDITOR: 'Editor',
  ROLE_ADMIN: 'Admin',
} as const;

export const UserStates = {
  active: 'Active',
  inactive: 'Inactive',
} as const;

export const Teams = {
  dev: 'DevTeam',
  po: 'PO',
  creative: 'Creative',
  ops: 'SysOps/Support',
} as const;

export const UserCapacities = {
  Sales: 4,
  Ckan: 25,
  Drupal: 25,
  Js: 15,
  Other: 5,
} as const;

// client account /////////////////////////////////////////////////////////////
export const Client = z.strictObject({
  ClientCode: z.string(),
  ClientName: z.string().trim(),
  XeroId: z.string(),
  InsightlyId: z.string(),
  AwsId: z.string(),
  Commissionable: zCommissionable,
  Notes: z.string(),
});

export type Client = z.infer<typeof Client>;

const Unchargeable = z.strictObject({
  Id: z.string(),
  Month: z.string(),
  Year: z.coerce.string(),
  Hours: z.number(),
  Notes: z.string(),
  ExpireMonth: z.string(),
  ExpireYear: z.string(),
  DueDate: z.string().default(''),
  Commissionable: zCommissionable,
  HourlyRate: zHourlyRate,
  Value: zHoursValue,
  Currency: zCurrency,
  Rate: z.number().default(1),
  Invoiced: z.boolean().default(false),
  Status: zInvoiceStatus.default(0),
  ProjectId: z.string().default(''),
  PurchaseOrder: zPurchaseOrder,
});

export type Unchargeable = z.infer<typeof Unchargeable>;

const Purchased = z.strictObject({
  Month: z.string(),
  Year: z.coerce.string(),
  Hours: z.number(),
  Notes: z.string(),
  ExpireMonth: z.string(),
  ExpireYear: z.coerce.string(),
  Invoiced: z.boolean().default(false),
  Commissionable: zCommissionable,
  Id: z.string(),
  Status: zInvoiceStatus,
  Value: zHoursValue,
  DueDate: z.string().default(''),
  HourlyRate: zHourlyRate,
  Currency: zCurrency,
  Rate: z.number().default(1),
  ProjectId: z.string().default(''),
  PurchaseOrder: zPurchaseOrder,
  InvoiceDate: z.string().default(''),
  idx: z.unknown().optional(),
  _owner: z.unknown().optional(),
});

export type Purchased = z.infer<typeof Purchased>;

const Profitable = z.strictObject({
  Id: z.string(),
  Month: z.string(),
  Year: z.coerce.string(),
  Hours: z.number(),
  Notes: z.string(),
  ExpireMonth: z.string(),
  ExpireYear: z.string(),
  DueDate: z.string(),
  Commissionable: zCommissionable,
  HourlyRate: zHourlyRate,
  Value: zHoursValue,
  Currency: zCurrency,
  Rate: z.number(),
  Status: zInvoiceStatus,
  ProjectId: z.string(),
  PurchaseOrder: zPurchaseOrder,
});

export type Profitable = z.infer<typeof Profitable>;

const Unforeseen = z.strictObject({
  Id: z.string(),
  Month: z.string(),
  Year: z.coerce.string(),
  Hours: z.number(),
  Notes: z.string(),
  ExpireMonth: z.string(),
  ExpireYear: z.string(),
  DueDate: z.string(),
  Commissionable: zCommissionable,
  HourlyRate: zHourlyRate,
  Value: zHoursValue,
  Currency: zCurrency,
  Rate: z.number(),
  Status: zInvoiceStatus,
  ProjectId: z.string(),
  PurchaseOrder: zPurchaseOrder,
});

export type Unforeseen = z.infer<typeof Unforeseen>;

const Milestone = z
  .strictObject({
    Status: zInvoiceStatus,
    Rate: z.number().default(1),
    Currency: zCurrency,
    CreatedAt: z.string().default('2012-01-01'),
    Value: zHoursValue,
    InvoiceDate: z.string().default(''),
    ProjectId: z.string().default(''),
    PurchaseOrder: zPurchaseOrder,
    Id: z.string(),
    DueDate: z.string(),
    Notes: z.string().default(''),
    Name: z.string(),
    HourlyRate: zHourlyRate,
    JobNumber: z.unknown().optional(),
  })
  .transform((user) => {
    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
    const { JobNumber, ...rest } = user;

    return rest;
  });
export type Milestone = z.infer<typeof Milestone>;

export const Product = z.strictObject({
  JobNumber: z.string(),
  Title: z.string().trim(),
  JiraTitle: z.string(),
  BudgetNote: zBudgetNote,
  Notes: z
    .string()
    .nullable()
    .default('')
    .transform((v) => v ?? ''),
  ClientCode: z.string(),
  ProductOwner: zProductOwner,
  Commissionable: zCommissionable,
  Status: zProductStatus.default('Unknown').transform((v) => v || 'Unknown'),
  PurchasedHours: z.array(Purchased).default(() => []),
  ProfitableHours: z.array(Profitable).default(() => []),
  Milestones: z
    .array(Milestone)
    .nullable()
    .default(() => [])
    .transform((v) => v ?? []),
  UnchargeableHours: z.array(Unchargeable).default(() => []),
});

export type Product = z.infer<typeof Product>;

const Addon = z.strictObject({
  AddonName: zAddonName,
  StartDate: z.string(),
  EndDate: z.string(),
  BillingCycle: zBillingCycle,
  Status: zAgreementStatus,
  InvoiceAmount: z.number(),
  InvoiceSeparately: z.coerce.boolean().default(false),
  NextInvoiceDate: z.string().default(''),
  Id: z.string(),
});

export type Addon = z.infer<typeof Addon>;

const Document = z.strictObject({
  DocumentId: z.string(),
  DocumentName: z.string(),
});

export type Document = z.infer<typeof Document>;

const Invoice = z.strictObject({
  InvoiceAmount: z.number(),
  InvoiceDate: z.string(),
  Notes: z.string(),
});

export type Invoice = z.infer<typeof Invoice>;

export const Agreement = z.strictObject({
  UnchargeableHours: z
    .array(Unchargeable)
    .nullish()
    .transform((v) => v ?? []),
  PurchasedHours: z.array(Purchased).default(() => []),
  ProfitableHours: z.array(Profitable).default(() => []),
  Addons: z
    .array(Addon)
    .nullish()
    .transform((v) => v ?? []),
  Documents: z.array(Document),
  UnforeseenHours: z
    .array(Unforeseen)
    .nullish()
    .transform((v) => v ?? [])
    .catch((v) => {
      console.warn('Unexpected value of unforeseen hours', v);
      return [];
    }),
  Invoices: z.array(Invoice).default(() => []),
  Status: zAgreementStatus,
  BillingCycle: zBillingCycle,
  ServiceType: zServiceType,
  AgreementNumber: z.string(),
  JobNumber: z.string(),
  HasSLA: z.boolean(),
  ExtendedLifetime: z.boolean().default(false),
  JiraTitle: z.string(),
  JiraLicence: z
    .number()
    .nullish()
    .transform((v) => v ?? 0),
  HostingAmount: z.number().default(0),
  ServiceAmount: z.number().default(0),
  StartDate: z.string(),
  InvoiceAmount: z.number(),
  SupportMaintenanceAmount: z.number().default(0),
  EndDate: z.string(),
  NextInvoiceDate: z.string().default(''),
  Title: z.string(),
  Notes: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  SupportHours: z.number(),
  ClientCode: z.string(),
  Commissionable: zCommissionable,
  ProductOwner: zProductOwner,
  BudgetNote: zBudgetNote,
});

export type Agreement = z.infer<typeof Agreement>;

export const Resource = z.strictObject({
  Id: z.string(),
  Title: z.string(),
  Url: z.string(),
});

export type Resource = z.infer<typeof Resource>;

export const HistoryRecord = z.strictObject({
  Id: z.string(),
  Time: z.string(),
  Data: z.discriminatedUnion('type', [
    z.strictObject({
      type: z.literal('update'),
      author: z.string().nullable(),
      diff: z.record(z.string(), z.tuple([z.any(), z.any()])).nullable().optional(),
    }),
    z.strictObject({
      type: z.literal('change'),
      author: z.string(),
      change: z.record(z.string(), z.any()).optional(),
      action: z.string(),
    }),
    z.strictObject({
      type: z.literal('create'),
      author: z.string(),
    }),
  ]),
});

export type HistoryRecord = z.infer<typeof HistoryRecord>;

export const User = z
  .strictObject({
    Role: z.literal(Object.keys(Roles) as (keyof typeof Roles)[]),
    JiraId: z.string().default(''),
    JsCapacity: z.number().default(UserCapacities.Js),
    Capacity: z.coerce
      .number()
      .nullish()
      .transform((v) => v ?? 0),
    FullName: z.string(),
    Team: z
      .literal(['', ...(Object.keys(Teams) as (keyof typeof Teams)[])])
      .nullish()
      .transform((v) => v ?? ''),
    State: z.literal(Object.keys(UserStates) as (keyof typeof UserStates)[]),
    Email: z.string(),
    SalesCapacity: z
      .number()
      .nullish()
      .transform((v) => v ?? UserCapacities.Sales),

    DrupalCapacity: z
      .number()
      .nullish()
      .transform((v) => v ?? UserCapacities.Drupal),

    CkanCapacity: z
      .number()
      .nullish()
      .transform((v) => v ?? UserCapacities.Ckan),

    at: z.unknown().optional(),
    OtherCapacity: z.number().default(UserCapacities.Other),

    HumanRole: z.unknown().optional(),
    HumanTeam: z.unknown().optional(),
    HumanState: z.unknown().optional(),
    _Role: z.unknown().optional(),
    _Team: z.unknown().optional(),
    _State: z.unknown().optional(),
  })
  .transform((user) => {
    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
    const { HumanRole, HumanState, HumanTeam, _State, _Team, _Role, ...rest } = user;

    return rest;
  });

export type User = z.infer<typeof User>;

export const Project = z.strictObject({
  Id: z.string(),
  Owner: z.string(),
  Title: z.string(),
  Description: z.string(),
  PurchaseOrder: z.number().default(0),
  StartDate: z.string().default(''),
  EndDate: z.string().default(''),
  PurchasedHours: z
    .array(Purchased)
    .length(0)
    .default(() => []),
  Milestones: z
    .array(Milestone)
    .length(0)
    .default(() => []),
});

export type Project = z.infer<typeof Project>;
